import React, {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type MutableRefObject,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Spinner from '#@/components/Spinner';
import { cosunoDomains, uriSchemes } from '#@/shared/constants';
import { omit } from '#@/shared/utils/javascript';
import { COLOR, mixins, sizes, textStyles } from '#@/shared/utils/styles';

import type { ButtonVariantV2 } from './types';
import { getDisabledColorFromButtonVariant, isInverted, isSecondary } from './utils';

interface StyledButtonBaseProps {
  linkTo?: LinkProps['to'];
  linkToExternal?: AnchorHTMLAttributes<HTMLAnchorElement>['href'];
}

interface StyledButtonProps extends StyledButtonBaseProps {
  forwardRef?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
  hasLeftIcon: boolean;
  hasRightIcon: boolean;
  iconOnly: boolean;
  working: boolean;
  disabled: boolean;
  $visuallyDisabled: boolean;
  buttonColor: COLOR;
  small: boolean;
  large: boolean;
  fullWidth: boolean;
  isTriggered: boolean;
  variant: ButtonVariantV2;
  align: 'flex-start' | 'center' | 'flex-end';
}

const StyledButtonBase: React.FC<
  StyledButtonProps &
    (ButtonHTMLAttributes<HTMLButtonElement> | AnchorHTMLAttributes<HTMLAnchorElement>)
> = ({ forwardRef, linkTo, linkToExternal, small, ...props }) => {
  const buttonProps = omit(props, [
    'hollow',
    'working',
    'hasLeftIcon',
    'hasRightIcon',
    'iconOnly',
    'buttonColor',
    'small',
    'large',
    'fullWidth',
    'variant',
    'isTriggered',
  ]) as ButtonHTMLAttributes<HTMLButtonElement>;

  if (linkTo) {
    const linkProps = buttonProps as AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <Link ref={forwardRef as MutableRefObject<HTMLAnchorElement>} to={linkTo} {...linkProps} />
    );
  }

  if (linkToExternal) {
    const { hostname } = new URL(
      uriSchemes.some((scheme) => linkToExternal.startsWith(scheme))
        ? linkToExternal
        : `http://${linkToExternal}`,
    );
    const isCosunoDomain = cosunoDomains.some((domain) => hostname.endsWith(domain));
    const linkProps = buttonProps as AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a
        ref={forwardRef as MutableRefObject<HTMLAnchorElement>}
        href={linkToExternal}
        rel={isCosunoDomain ? 'noopener' : 'noopener noreferrer'}
        {...linkProps}
      >
        {linkProps.children}
      </a>
    );
  }

  // In this specific case we do not want to use an explicit type, instead we
  // rely on the consumer of this component to set it.
  // eslint-disable-next-line react/button-has-type
  return <button ref={forwardRef as MutableRefObject<HTMLButtonElement>} {...buttonProps} />;
};

const iconOnlyStyles = (props: StyledButtonProps) => css`
  --padding-horizontal: 0px;
  width: ${sizes.buttonHeight}px;

  /* Always keep the square shape */
  flex-shrink: 0;

  ${props.small &&
  css`
    width: ${sizes.buttonHeightSmall}px;
  `}
  ${props.large &&
  css`
    width: ${sizes.buttonHeightLarge}px;
  `}
`;

export const StyledSpinner = styled(Spinner)`
  display: flex;
`;

const filledStyles = (props: StyledButtonProps) => {
  const inverted = isInverted(props.variant);
  const activeBgColor = inverted ? 'rgb(189,203,232)' : mixins.darken(props.buttonColor, 0.3);

  const activeStyles = css`
    background: ${activeBgColor};
    border-color: ${activeBgColor};
    outline: none;
  `;

  const hoverBackgroundColor = inverted
    ? 'rgb(233,238,247)'
    : mixins.darken(props.buttonColor, 0.15);

  return css`
    background: ${props.buttonColor};
    border: 1px solid ${props.buttonColor};
    color: ${inverted ? COLOR.brand : COLOR.white};

    ${props.$visuallyDisabled && !props.working
      ? css`
          background: ${COLOR.backgroundMedium};
          border: 1px solid transparent;
          color: ${getDisabledColorFromButtonVariant(props.variant)};
        `
      : css`
          &:hover {
            background: ${hoverBackgroundColor};
            border: 1px solid ${hoverBackgroundColor};
          }
          &:active {
            ${activeStyles}
          }
          &:focus-visible {
            border: 1px solid ${inverted ? COLOR.brand : COLOR.highlightNeon};
            outline: 2px solid ${mixins.rgba(inverted ? COLOR.white : COLOR.brand, 0.2)};
          }
        `}

    ${props.isTriggered && activeStyles}
  `;
};

const hollowStyles = (props: StyledButtonProps) => {
  const inverted = isInverted(props.variant);
  const activeStyles = css`
    border-color: transparent;
    background: ${mixins.rgba(props.buttonColor, 0.1)};
    outline: none;
    color: ${props.buttonColor};
  `;

  return css`
    --base-color: ${props.variant === 'secondary' ? COLOR.textSecondary : props.buttonColor};

    border: 1px solid transparent;
    color: var(--base-color);

    ${props.$visuallyDisabled && !props.working
      ? css`
          border: 1px solid transparent;
          color: ${getDisabledColorFromButtonVariant(props.variant)};
        `
      : css`
          &:hover {
            background: ${mixins.rgba(props.buttonColor, 0.05)};
            color: ${props.buttonColor};
          }
          &:active {
            ${activeStyles}
          }
          &:focus-visible {
            border: 1px solid ${inverted ? COLOR.white : COLOR.highlightNeon};
            outline: 2px solid ${mixins.rgba(inverted ? COLOR.white : COLOR.brand, 0.2)};
          }
        `}

    ${props.isTriggered && activeStyles}

    ${props.variant === 'secondaryBrand' &&
    css`
      &:hover,
      &:active {
        color: ${COLOR.highlightNeon};
      }
    `}
  `;
};

export const StyledButton = styled(StyledButtonBase)<StyledButtonProps>`
  // --padding-horizontal is required by the button group for alignment with parent component text by negative margins
  --padding-horizontal: 13px;

  display: inline-flex;
  gap: ${sizes.buttonGap}px;
  align-items: center;
  justify-content: ${(props) => props.align};
  height: ${sizes.buttonHeight}px;
  padding: 0 var(--padding-horizontal);
  vertical-align: middle;
  white-space: nowrap;
  text-align: center;
  border-radius: 8px;
  appearance: none !important;
  ${mixins.clickable()};
  ${textStyles.labelMedium500};
  ${(props) => (isSecondary(props.variant) ? hollowStyles : filledStyles)};
  ${(props) =>
    (props.$visuallyDisabled || props.working) &&
    css`
      cursor: default;
    `}
  ${(props) =>
    (props.disabled || props.working) &&
    css`
      /* Disable clicks also on non-<button> Buttons, such as <Button linkTo={...}> */
      pointer-events: none;
    `}
  ${(props) =>
    props.$visuallyDisabled &&
    !props.working &&
    css`
      opacity: ${isInverted(props.variant) ? 0.8 : 0.4} !important;
    `};
  ${(props) =>
    props.small &&
    css`
      gap: 5px;
      height: ${sizes.buttonHeightSmall}px;
      border-radius: 6px;
      --padding-horizontal: 8px;
    `};
  ${(props) =>
    props.large &&
    css`
      gap: 10px;
      height: ${sizes.buttonHeightLarge}px;
      border-radius: 10px;
      ${textStyles.labelLarge500};
      --padding-horizontal: 19px;
    `};
  ${(props) =>
    props.fullWidth &&
    css`
      width: 100%;
    `};
  ${(props) => props.iconOnly && iconOnlyStyles(props)};
`;
